import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STEPS = [
  "Extracting text",
  "Identifying sections",
  "Structuring TRAM template",
  "Finalising preview",
];

export default function Dashboard() {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(-1);
  const inputRef = useRef(null);
  const nav = useNavigate();

  const onFile = (f) => {
    if (!f) return;
    const ok = /\.(pdf|docx)$/i.test(f.name);
    if (!ok) return toast.error("Only PDF or DOCX files are supported");
    setFile(f);
  };

  const generate = async () => {
    if (!file) return toast.error("Choose a CV file first");
    setBusy(true);
    setStep(0);
    let stepTimer = null;
    const runSteps = () => {
      let s = 0;
      stepTimer = setInterval(() => {
        s += 1;
        setStep(Math.min(s, STEPS.length - 1));
      }, 1200);
    };
    runSteps();
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/cv/parse", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      clearInterval(stepTimer);
      setStep(STEPS.length - 1);
      // Save initial version so it appears in library
      const save = await api.post("/cv", { data: r.data.cv });
      toast.success("CV parsed into TRAM template");
      nav(`/editor/${save.data.id}`);
    } catch (e) {
      clearInterval(stepTimer);
      toast.error(e?.response?.data?.detail || "Failed to parse CV");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-10">
        <div className="overline mb-2">TRAM · CV Generator</div>
        <h1 className="font-serif-display text-5xl font-semibold text-[#0f1c3f] tracking-tight">
          Reformat any CV into the TRAM template.
        </h1>
        <p className="mt-3 text-[15px] text-[#5a6578] max-w-2xl leading-relaxed">
          Upload a candidate PDF or DOCX. Our AI extracts every section, structures it into your
          signature template, and hands you an editable preview ready to download.
        </p>
      </div>

      <div className="grid lg:grid-cols-[1.35fr_1fr] gap-8">
        <div
          data-testid="cv-upload-dropzone"
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            onFile(e.dataTransfer.files?.[0]);
          }}
          className={`relative bg-white border-2 border-dashed rounded-2xl p-12 text-center transition-colors flex flex-col items-center justify-center min-h-[520px] ${
            dragOver ? "border-[#0f1c3f] bg-[#f0ece1]" : "border-[#cfc8ba]"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.docx"
            hidden
            data-testid="cv-file-input"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
          <div className="w-14 h-14 rounded-full bg-[#0f1c3f] text-white flex items-center justify-center mx-auto">
            <UploadCloud size={26} />
          </div>
          <h3 className="mt-5 font-serif-display text-2xl text-[#0f1c3f] font-semibold">
            Drop a CV to begin
          </h3>
          <p className="text-sm text-[#5a6578] mt-1">
            PDF or DOCX, up to 10MB. We&apos;ll do the rest.
          </p>

          {file && (
            <div className="mt-6 inline-flex items-center gap-3 border border-[#e2ddd3] bg-[#faf8f5] px-4 py-2 rounded-md">
              <FileText size={16} className="text-[#0f1c3f]" />
              <span className="text-sm text-[#0f1c3f] font-medium" data-testid="selected-file">
                {file.name}
              </span>
            </div>
          )}

          <div className="mt-8 flex items-center justify-center gap-3">
            <Button
              onClick={() => inputRef.current?.click()}
              variant="outline"
              className="border-[#cfc8ba] text-[#0f1c3f]"
              data-testid="choose-file-btn"
            >
              Choose file
            </Button>
            <Button
              onClick={generate}
              disabled={!file || busy}
              className="bg-[#0f1c3f] hover:bg-[#0a122a] text-white"
              data-testid="generate-btn"
            >
              <Sparkles size={15} className="mr-1.5" />
              {busy ? "Generating…" : "Generate TRAM CV"}
            </Button>
          </div>

          {busy && (
            <div className="mt-8 max-w-md mx-auto text-left space-y-2" data-testid="progress-steps">
              {STEPS.map((s, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      i < step
                        ? "bg-[#2e7d32]"
                        : i === step
                          ? "bg-[#0f1c3f] animate-pulse"
                          : "bg-[#cfc8ba]"
                    }`}
                  />
                  <span
                    className={`text-sm ${
                      i <= step ? "text-[#0f1c3f]" : "text-[#8a94a6]"
                    }`}
                  >
                    {s}
                    {i === step ? "…" : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="bg-white border border-[#e2ddd3] rounded-2xl p-8">
          <div className="overline mb-3">How it works</div>
          <ol className="space-y-5">
            {[
              ["Upload", "Drop the candidate's CV (PDF or DOCX)."],
              ["Parse", "AI extracts every section and normalises it."],
              ["Edit", "Refine any field in the split-screen studio."],
              ["Export", "Download a print-ready TRAM PDF."],
            ].map(([t, d], i) => (
              <li key={i} className="flex gap-4">
                <div className="w-8 h-8 rounded-md border border-[#0f1c3f] text-[#0f1c3f] flex items-center justify-center font-mono-tram text-[13px]">
                  0{i + 1}
                </div>
                <div>
                  <div className="text-[#0f1c3f] font-semibold text-sm tracking-wide">{t}</div>
                  <div className="text-sm text-[#5a6578] leading-relaxed">{d}</div>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-8 pt-6 border-t border-[#e2ddd3]">
            <div className="overline mb-2">Empty template</div>
            <p className="text-sm text-[#5a6578] mb-3">
              Prefer to build from scratch? Start with a blank TRAM CV.
            </p>
            <Button
              variant="outline"
              className="border-[#cfc8ba] w-full"
              data-testid="start-blank-btn"
              onClick={async () => {
                try {
                  const r = await api.post("/cv", { data: {} });
                  nav(`/editor/${r.data.id}`);
                } catch {
                  toast.error("Could not start blank CV");
                }
              }}
            >
              Start blank CV
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
}
